import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Toggle,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAdminProgram, useUpdateProgramme } from "../../lib/queries";
import type { AdminProgram } from "../../lib/types";
import { Trans, useTranslation } from "react-i18next";

/**
 * The programme's terms, which used to be a row somebody changed with `psql`.
 *
 * ---------------------------------------------------------------------------
 * What changes and what does not
 * ---------------------------------------------------------------------------
 * **Nothing here is retroactive.** The rate and the cap are copied onto each
 * commission as it is earned, so lowering the rate changes what the next
 * referral is worth and never what an affiliate has already been told they
 * earned. That is a property of the schema, not a promise this form makes, and
 * it is the reason this screen can exist at all — a form that silently rewrote
 * people's balances would not be safe to put in a browser.
 *
 * The hold window is the one exception worth stating: it is read when a
 * commission is created, so commissions already ripening keep the window they
 * were created under.
 *
 * ---------------------------------------------------------------------------
 * Currency units, not micros
 * ---------------------------------------------------------------------------
 * Everything on this form is in dollars. The API converts at the route, so
 * nobody typing a payout minimum has to think in millionths — which is exactly
 * the kind of arithmetic that puts a factor of a thousand into a live setting.
 */
export function AdminTermsPage() {
  const { t } = useTranslation("admin");
  const program = useAdminProgram();

  return (
    <>
      <PageHeader
        title={t("terms.title")}
        description={t("terms.description")}
      />
      {/*
       * The published Terms restate these figures, and this form cannot change
       * that page.
       *
       * Section 7 of the Terms says 50%, $100, 30 days, $25 and 60 days in
       * words, because a programme that pays real money needs published terms
       * and "see the dashboard" is not a term. So the two are coupled and
       * nothing enforces it: change a number here and the Terms are wrong until
       * somebody edits them, and a customer relies on the page rather than on
       * this form.
       *
       * Said here rather than in a comment nobody reading the screen will see.
       */}
      <Alert tone="warn">
        <Trans
          ns="admin"
          i18nKey="terms.legal"
          components={{
            a: (
              <a
                className="underline transition-colors duration-200 ease-smooth hover:text-fg"
                href="/legal/terms#affiliate-programme"
                target="_blank"
                rel="noreferrer"
              />
            ),
          }}
        />
      </Alert>

      {program.isPending ? (
        <LoadingBlock />
      ) : program.error ? (
        <Alert>{program.error.message}</Alert>
      ) : (
        <TermsForm program={program.data} />
      )}
    </>
  );
}

/*
 * The form is `useState` seeded from the query, which is the one place this app
 * copies server state into component state — and it is what a form *is*. The
 * rule it does follow: the copy is made once, keyed on the loaded programme, so
 * a refetch cannot overwrite half-typed input. `useAdminProgram` also turns off
 * focus refetching for the same reason.
 */
function TermsForm({ program }: { program: AdminProgram }) {
  const { t } = useTranslation("admin");
  const update = useUpdateProgramme();
  const [form, setForm] = useState(() => ({
    commissionPercent: String(program.commissionBps / 100),
    commissionCap: String(program.commissionCapMicros / 1_000_000),
    minPayout: String(program.minPayoutMicros / 1_000_000),
    holdDays: String(program.holdDays),
    cookieDays: String(program.cookieDays),
    acceptingReferrals: program.acceptingReferrals,
    requiresApproval: program.requiresApproval,
    termsUrl: program.termsUrl ?? "",
  }));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
    setSaved(false);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        commissionPercent: Number(form.commissionPercent),
        commissionCap: Number(form.commissionCap),
        minPayout: Number(form.minPayout),
        holdDays: Number(form.holdDays),
        cookieDays: Number(form.cookieDays),
        acceptingReferrals: form.acceptingReferrals,
        requiresApproval: form.requiresApproval,
        termsUrl: form.termsUrl.trim() || null,
      });
      setSaved(true);
    } catch (cause) {
      /*
       * The API's own sentence, not a mapped one. Every refusal here is a
       * bound — "a commission rate over 90% is almost certainly a typo" — and
       * the bound is the useful part.
       */
      setError(cause instanceof ApiError ? cause.message : t("terms.saveFailed"));
    }
  }

  return (
    <form onSubmit={(event) => void submit(event)} className="space-y-6">
      <Card>
        <CardHeader
          title={t("terms.commission.title")}
          description={t("terms.commission.description")}
        />
        <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
          <Field
            label={t("terms.rate")}
            htmlFor="rate"
            hint={t("terms.rateHint")}
          >
            <Input
              id="rate"
              type="number"
              inputMode="decimal"
              min={0}
              max={90}
              step={0.5}
              value={form.commissionPercent}
              onChange={(event) => set("commissionPercent", event.target.value)}
            />
          </Field>

          <Field
            label={t("terms.cap")}
            htmlFor="cap"
            hint={t("terms.capHint")}
          >
            <Input
              id="cap"
              type="number"
              inputMode="decimal"
              min={1}
              step={1}
              value={form.commissionCap}
              onChange={(event) => set("commissionCap", event.target.value)}
            />
          </Field>

          <Field
            label={t("terms.hold")}
            htmlFor="hold"
            hint={t("terms.holdHint")}
          >
            <Input
              id="hold"
              type="number"
              inputMode="numeric"
              min={0}
              max={180}
              step={1}
              value={form.holdDays}
              onChange={(event) => set("holdDays", event.target.value)}
            />
          </Field>

          <Field
            label={t("terms.minimum")}
            htmlFor="min"
            hint={t("terms.minimumHint")}
          >
            <Input
              id="min"
              type="number"
              inputMode="decimal"
              min={1}
              step={1}
              value={form.minPayout}
              onChange={(event) => set("minPayout", event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t("terms.attribution.title")}
          description={t("terms.attribution.description")}
        />
        <div className="grid gap-4 px-4 py-4 sm:grid-cols-2">
          <Field
            label={t("terms.cookie")}
            htmlFor="cookie"
            hint={t("terms.cookieHint")}
          >
            <Input
              id="cookie"
              type="number"
              inputMode="numeric"
              min={1}
              max={365}
              step={1}
              value={form.cookieDays}
              onChange={(event) => set("cookieDays", event.target.value)}
            />
          </Field>

          <Field
            label={t("terms.termsPage")}
            htmlFor="terms"
            hint={t("terms.termsPageHint")}
          >
            <Input
              id="terms"
              type="url"
              placeholder={t("terms.termsPagePlaceholder")}
              value={form.termsUrl}
              onChange={(event) => set("termsUrl", event.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card>
        <CardHeader title={t("terms.door.title")} description={t("terms.door.description")} />
        <div className="space-y-3 px-4 py-4">
          <Toggle
            label={t("terms.accepting")}
            hint={t("terms.acceptingHint")}
            checked={form.acceptingReferrals}
            onChange={(value) => set("acceptingReferrals", value)}
          />
          <Toggle
            label={t("terms.approval")}
            hint={t("terms.approvalHint")}
            checked={form.requiresApproval}
            onChange={(value) => set("requiresApproval", value)}
          />
        </div>
      </Card>

      {error && <Alert>{error}</Alert>}

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" loading={update.isPending}>
          {t("terms.save")}
        </Button>
        {/* What changed last and when, because this row is shared by everybody
            with the role and "did somebody already do this" is the question. */}
        <p className="text-[12px] text-fg-subtle" aria-live="polite">
          {saved ? t("terms.saved") : t("terms.lastChanged", { date: formatDate(program.updatedAt) })}
        </p>
      </div>
    </form>
  );
}
